import { useTheme } from '@mui/material/styles';
import { useState } from 'react';
import SharedButton from '../../shared/buttons/shared-button';
import { useUserSettings } from '../../utils/use-user-settings';
import FlaggedSectionHeader from './flagged-section-header';
import TileLibraryTile from './tile-library-tile';
import {
  CommandDashboardTileDefinition,
  CommandDashboardTileKey,
  describeRequiredPermissions,
  isTilePermitted,
} from './tile-config';

import Box from '@mui/material/Box';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import IconButton from '@mui/material/IconButton';
import MenuList from '@mui/material/MenuList';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Stack from '@mui/material/Stack';
import Add from '@mui/icons-material/Add';
import Close from '@mui/icons-material/Close';

interface TileLibraryProps {
  availableTiles: CommandDashboardTileDefinition[];
  onAdd: (key: CommandDashboardTileKey) => void;
  open?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
}

/**
 * Padding of the card the toolbar's Add Tiles button sits in, which insets that
 * button from the card's own edges.
 */
const CARD_PADDING = 16;

/** Breathing room between the bottom of the card and the panel. */
const PANEL_GAP = 8;

export function AddTilesButton({
  disabled,
  onClick,
}: {
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <SharedButton startIcon={<Add />} disabled={disabled} onClick={onClick}>
      Add Tiles
    </SharedButton>
  );
}

export default function TileLibrary({
  availableTiles,
  onAdd,
  open: openProp,
  onOpen,
  onClose,
}: TileLibraryProps) {
  const theme = useTheme();
  const outlineLight = theme.palette.semantic.outline.light;
  const surfaceBg1 = theme.palette.semantic.surface.bg1;
  const textTitle = theme.palette.semantic.text.title;
  const roles = useUserSettings();
  const [buttonEl, setButtonEl] = useState<HTMLElement | null>(null);
  const [internalOpen, setInternalOpen] = useState(false);
  const isOpen = openProp ?? internalOpen;

  const handleOpen = () => {
    onOpen?.();
    if (openProp === undefined) {
      setInternalOpen(true);
    }
  };

  const handleClose = () => {
    onClose?.();
    if (openProp === undefined) {
      setInternalOpen(false);
    }
  };

  const disabledReasonFor = (tile: CommandDashboardTileDefinition) =>
    isTilePermitted(tile, roles)
      ? undefined
      : `You need "${describeRequiredPermissions(
          tile
        )}" access to add this tile`;

  const handleAdd = (key: CommandDashboardTileKey) => {
    handleClose();
    onAdd(key);
  };

  return (
    <>
      <Box ref={setButtonEl} sx={{ display: 'inline-flex' }}>
        <AddTilesButton
          disabled={availableTiles.length === 0}
          onClick={handleOpen}
        />
      </Box>
      <Popper
        anchorEl={buttonEl}
        open={isOpen}
        placement="bottom-end"
        modifiers={[
          {
            name: 'offset',
            options: { offset: [CARD_PADDING, CARD_PADDING + PANEL_GAP] },
          },
        ]}
        sx={{ zIndex: theme.zIndex.modal }}
      >
        <ClickAwayListener onClickAway={handleClose}>
          <Paper
            sx={{
              width: 360,
              overflow: 'hidden',
              border: `0.5px solid ${outlineLight}`,
            }}
          >
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="flex-start"
              sx={{
                p: '10px 8px',
                alignSelf: 'stretch',
                borderBottom: '1px solid',
                borderColor: outlineLight,
                bgcolor: surfaceBg1,
              }}
            >
              <FlaggedSectionHeader
                title="Tile Library"
                titleVariant="subtitle2"
                titleSx={{ color: textTitle, fontWeight: 600 }}
                helperText="Drag to add tiles to your dashboard."
              />
              <IconButton
                size="small"
                aria-label="Close tile library"
                onClick={handleClose}
                sx={{ color: theme.palette.semantic.icon.default }}
              >
                <Close fontSize="small" />
              </IconButton>
            </Stack>
            <MenuList
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 2,
                px: 2.5,
                py: 2,
                // Matches the app's own page background exactly in light mode - surface.bg3
                // reads a shade darker than intended here.
                bgcolor:
                  theme.palette.mode === 'dark'
                    ? theme.palette.semantic.surface.bg3
                    : '#f2f2f2',
              }}
            >
              {availableTiles.map((tile) => (
                <TileLibraryTile
                  key={tile.key}
                  tile={tile}
                  onAdd={handleAdd}
                  disabledReason={disabledReasonFor(tile)}
                />
              ))}
            </MenuList>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </>
  );
}
